// Global Modal Manager
// This utility allows triggering modals from anywhere in the application

type ModalType = 'available-soon' | 'alert' | 'confirm';

interface ModalState {
  type: ModalType | null;
  isOpen: boolean;
  data?: any; // For passing message to alert modal
}

class GlobalModalManager {
  private listeners: ((state: ModalState) => void)[] = [];
  private state: ModalState = { type: null, isOpen: false, data: null };

  subscribe(callback: (state: ModalState) => void) {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  openModal(type: ModalType, data?: any) {
    this.state = { type, isOpen: true, data };
    this.notifyListeners();
  }

  closeModal() {
    this.state = { type: null, isOpen: false, data: null };
    this.notifyListeners();
  }

  getState() {
    return this.state;
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.state));
  }
}

// Create singleton instance
const globalModalManager = new GlobalModalManager();

// Make it available globally on window for use from Rails views
if (typeof window !== 'undefined') {
  (window as any).globalModalManager = globalModalManager;

  // Add convenience function for showing "available soon" modal
  (window as any).showAvailableSoonModal = () => {
    globalModalManager.openModal('available-soon');
  };

  // Add convenience function for showing alert modal
  (window as any).showAlertModal = (message: string) => {
    globalModalManager.openModal('alert', { message });
  };

  // Add convenience function for showing confirm modal
  (window as any).showConfirmModal = (message: string, onConfirm: () => void, options?: { confirmText?: string; cancelText?: string; variant?: 'default' | 'danger' }) => {
    globalModalManager.openModal('confirm', {
      message,
      onConfirm,
      confirmText: options?.confirmText,
      cancelText: options?.cancelText,
      variant: options?.variant || 'default'
    });
  };
}

export default globalModalManager;
